import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DataQualityDashboardDto, DataQualityIssueDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtBRL } from "../lib/format";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconAlertTriangle, IconCheck, IconChevronDown, IconRefresh } from "../components/icons";
import { PageHeader } from "../components/PageHeader";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { KpiTile } from "../components/intelligence/KpiTile";
import { AttentionPanel } from "../components/intelligence/AttentionPanel";
import { SystemHero, HeroNumber } from "../components/intelligence/SystemHero";
import {
  CHECKS, SEVERITY_LABEL, checkOf, dataQualityAttention, fmtRunAt, groupIssues, plateOf, recordLabel,
  type IssueGroup, type Severity,
} from "../lib/data-quality";

type Issues = { issues: DataQualityIssueDto[] };
type Status = DataQualityIssueDto["status"];

type Col = { label: string; right?: boolean; cell: (i: DataQualityIssueDto) => ReactNode };

const ev = (i: DataQualityIssueDto, k: string) => i.evidenceJson?.[k];
const n = (v: unknown) => (typeof v === "number" ? v : null);
const date = (v: unknown) => (typeof v === "string" ? v.slice(0, 10).split("-").reverse().join("/") : "—");
const int = (v: unknown) => (n(v) != null ? Math.round(n(v)!).toLocaleString("pt-BR") : "—");
const plateCell = (i: DataQualityIssueDto) => {
  const plate = plateOf(i);
  return plate ? <Link to={`/frota/placa/${plate}`} className="font-medium text-tm-700 underline">{plate}</Link> : <span className="font-medium text-gray-800">{recordLabel(i)}</span>;
};

/**
 * Colunas das verificações que mais aparecem, lidas da evidência gravada pela
 * varredura. As demais (e linhas antigas sem evidência) mostram a descrição.
 */
const COLUMNS: Record<string, Col[]> = {
  long_overdue_high_value: [
    { label: "Cliente", cell: (i) => <span className="font-medium text-gray-800">{typeof ev(i, "customer") === "string" ? String(ev(i, "customer")) : "—"}</span> },
    { label: "Título no ERP", cell: (i) => <span className="whitespace-nowrap tabular-nums text-gray-600">{i.externalKey ?? "—"}</span> },
    { label: "Vencido em", cell: (i) => <span className="tabular-nums">{date(ev(i, "dueDate"))}</span> },
    { label: "Dias vencido", right: true, cell: (i) => <span className="tabular-nums">{int(ev(i, "daysOverdue"))}</span> },
    { label: "Saldo", right: true, cell: (i) => <span className="whitespace-nowrap font-semibold tabular-nums text-gray-900">{n(ev(i, "openAmount")) != null ? fmtBRL(n(ev(i, "openAmount"))!) : "—"}</span> },
  ],
  implausible_fuel_odometer: [
    { label: "Placa", cell: plateCell },
    { label: "Data", cell: (i) => <span className="tabular-nums">{date(ev(i, "date"))}</span> },
    { label: "Odômetro (anterior → lançado)", cell: (i) => <span className="whitespace-nowrap tabular-nums text-gray-600">{int(ev(i, "odometerPrevious"))} → {int(ev(i, "odometer"))}</span> },
    { label: "Km desde o anterior", right: true, cell: (i) => <span className="font-semibold tabular-nums text-gray-900">{int(ev(i, "km"))}</span> },
    { label: "Km/l resultante", right: true, cell: (i) => <span className="tabular-nums">{n(ev(i, "kmPerLiter")) != null ? n(ev(i, "kmPerLiter"))!.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—"}</span> },
  ],
};

const DEFAULT_COLUMNS: Col[] = [
  { label: "Registro", cell: plateCell },
  { label: "Detalhe", cell: (i) => <span className="text-gray-600">{i.description}</span> },
];

/** Colunas estruturadas só quando todas as linhas trazem a evidência nova. */
function columnsFor(group: IssueGroup): Col[] {
  const cols = COLUMNS[group.check.type];
  const key = group.check.type === "implausible_fuel_odometer" ? "plate" : "customer";
  return cols && group.issues.every((i) => i.evidenceJson && key in i.evidenceJson) ? cols : DEFAULT_COLUMNS;
}

const SEVERITY_TONE: Record<Severity, string> = {
  critical: "bg-danger-50 text-danger-700",
  warning: "bg-warning-50 text-warning-700",
  info: "bg-info-50 text-info-700",
};

export function DataQualityPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" && !user.isDemo;
  const qc = useQueryClient();
  const [tab, setTab] = useState<"open" | "treated" | "checks">("open");

  const dash = useQuery({ queryKey: ["data-quality", "dashboard"], queryFn: () => api.get<DataQualityDashboardDto>("/data-quality/dashboard"), staleTime: 120_000 });
  const open = useQuery({ queryKey: ["data-quality", "issues", "open"], queryFn: () => api.get<Issues>("/data-quality/issues?status=open&limit=500"), staleTime: 120_000 });
  const treated = useQuery({ queryKey: ["data-quality", "issues", "treated"], queryFn: () => api.get<Issues>("/data-quality/issues?status=resolved,ignored&limit=500"), staleTime: 120_000 });

  const refresh = () => qc.invalidateQueries({ queryKey: ["data-quality"] });
  const run = useMutation({ mutationFn: () => api.post("/data-quality/run"), onSuccess: refresh });
  const setStatus = useMutation({ mutationFn: ({ id, status }: { id: string; status: Status }) => api.patch(`/data-quality/issues/${id}`, { status }), onSuccess: refresh });

  const groups = open.data ? groupIssues(open.data.issues) : [];
  const failing = new Set(groups.map((g) => g.check.type));
  const passing = CHECKS.filter((c) => !failing.has(c.type));
  const critical = groups.filter((g) => g.check.severity === "critical").reduce((n, g) => n + g.issues.length, 0);
  const s = dash.data?.summary;
  const error = dash.error ?? open.error;
  const loading = dash.isLoading || open.isLoading;

  return <div className="space-y-6">
    <PageHeader
      title="Qualidade dos Dados"
      subtitle="Lançamentos do ERP com inconsistência que distorcem os indicadores"
      actions={canEdit ? <button className="btn-secondary" disabled={run.isPending} onClick={() => run.mutate()}>
        <IconRefresh className={`h-4 w-4 ${run.isPending ? "animate-spin" : ""}`} />{run.isPending ? "Verificando…" : "Verificar agora"}
      </button> : undefined}
    />

    {error && <QueryErrorBanner what="a qualidade dos dados" error={error} onRetry={refresh} />}
    {run.error && <p role="alert" className="text-sm text-danger-600">{run.error instanceof ApiError ? run.error.message : "Não foi possível rodar a verificação."}</p>}

    {!error && <SystemHero loading={loading}
      status={critical ? "critical" : s?.open ? "warning" : "ok"}
      statusText={groups.length ? `${groups.length} verificaç${groups.length > 1 ? "ões" : "ão"} com problema` : "tudo certo"}
      headline={s ? (s.open
        ? <>A última verificação encontrou <HeroNumber>{s.open.toLocaleString("pt-BR")}</HeroNumber> {s.open === 1 ? "lançamento" : "lançamentos"} do ERP com problema.</>
        : <>Nenhum lançamento do ERP com problema na última verificação.</>) : null}
      detail={s ? `${passing.length} de ${CHECKS.length} verificações passaram.${dash.data?.lastRunAt ? ` Verificado em ${fmtRunAt(dash.data.lastRunAt)}; roda todo dia às 02h.` : ""}` : undefined} />}

    {!error && <AttentionPanel points={dataQualityAttention(groups)} loading={loading}
      empty={{ title: "Nenhum problema em aberto", text: `As ${CHECKS.length} verificações passaram na última varredura.` }} />}

    <section aria-labelledby="indicadores-titulo" className="space-y-3">
      <h2 id="indicadores-titulo" className="font-display font-semibold text-lg text-gray-900">Indicadores de qualidade</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiTile label="Problemas em aberto" value={s ? s.open.toLocaleString("pt-BR") : "—"} loading={loading}
          tone={s?.open ? "warning" : null}
          footnote={groups.length ? `Em ${groups.length} de ${CHECKS.length} verificações` : "Nenhuma verificação com problema"} />
        <KpiTile label="Críticos" value={open.data ? critical.toLocaleString("pt-BR") : "—"} loading={loading}
          tone={critical ? "critical" : null}
          footnote={critical ? "Distorcem valores financeiros" : "Nenhum problema crítico"} />
        <KpiTile label="Verificações sem problema" value={open.data ? `${passing.length} de ${CHECKS.length}` : "—"} loading={loading}
          footnote="CT-e, cartas frete, acertos, contas e abastecimentos" />
        <KpiTile label="Tratados" value={s ? (s.resolved + s.ignored).toLocaleString("pt-BR") : "—"} loading={loading}
          footnote={s ? `${s.resolved.toLocaleString("pt-BR")} resolvido${s.resolved === 1 ? "" : "s"} · ${s.ignored.toLocaleString("pt-BR")} ignorado${s.ignored === 1 ? "" : "s"}` : undefined} />
      </div>
    </section>

    <h2 className="font-display font-semibold text-lg text-gray-900">Registros para corrigir</h2>
    <section className="card overflow-hidden">
      <div className="flex flex-wrap gap-1 border-b border-surface-border px-5" role="tablist" aria-label="Qualidade dos dados">
        {([["open", `Em aberto${s ? ` (${s.open.toLocaleString("pt-BR")})` : ""}`], ["treated", `Tratados${s ? ` (${(s.resolved + s.ignored).toLocaleString("pt-BR")})` : ""}`], ["checks", "O que é verificado"]] as const).map(([key, label]) =>
          <button key={key} role="tab" aria-selected={tab === key} aria-controls={`qualidade-${key}`} id={`tab-${key}`} onClick={() => setTab(key)} className={`border-b-2 px-4 py-4 text-sm font-semibold ${tab === key ? "border-tm-500 text-tm-700" : "border-transparent text-gray-500 hover:text-gray-900"}`}>{label}</button>)}
      </div>
      <div role="tabpanel" id={`qualidade-${tab}`} aria-labelledby={`tab-${tab}`} className="p-5">
        {tab === "open" && (open.isLoading ? <p role="status" className="text-sm text-gray-500">Carregando…</p>
          : groups.length === 0 ? <p className="flex items-center gap-2 text-sm text-gray-600"><IconCheck className="h-5 w-5 text-tm-600" />Nenhum problema em aberto. Todas as {CHECKS.length} verificações passaram.</p>
          : <div className="space-y-4">
              {groups.map((g, i) => <CheckGroup key={g.check.type} group={g} defaultOpen={i === 0} canEdit={canEdit} busyId={setStatus.isPending ? setStatus.variables?.id : undefined} onStatus={(id, status) => setStatus.mutate({ id, status })} />)}
              {passing.length > 0 && <p className="text-xs text-gray-500">As outras {passing.length} verificações não acharam problema. <button className="font-medium text-tm-700 underline" onClick={() => setTab("checks")}>Ver o que é verificado</button></p>}
            </div>)}
        {tab === "treated" && <Treated issues={treated.data?.issues} loading={treated.isLoading} canEdit={canEdit} busyId={setStatus.isPending ? setStatus.variables?.id : undefined} onReopen={(id) => setStatus.mutate({ id, status: "open" })} />}
        {tab === "checks" && <div className="grid gap-3 lg:grid-cols-2">
          {CHECKS.map((c) => {
            const n = groups.find((g) => g.check.type === c.type)?.issues.length ?? 0;
            return <article key={c.type} className="rounded-xl border border-surface-border p-4">
              <h3 className="flex items-start gap-2 text-sm font-semibold text-gray-900">
                {n ? <IconAlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600" /> : <IconCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-tm-600" />}
                <span className="flex-1">{c.title}</span>
                <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${n ? SEVERITY_TONE[c.severity] : "bg-tm-50 text-tm-700"}`}>{n ? `${n.toLocaleString("pt-BR")} em aberto` : "OK"}</span>
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-gray-600">{c.why}</p>
            </article>;
          })}
          <p className="text-xs text-gray-500 lg:col-span-2">Cada verificação lista até 500 registros (200 no caso dos títulos vencidos, os de maior saldo). Itens ignorados não voltam nas próximas verificações; itens resolvidos que continuarem errados no ERP reaparecem em aberto.</p>
        </div>}
      </div>
    </section>
  </div>;
}

function CheckGroup({ group, defaultOpen, canEdit, busyId, onStatus }: { group: IssueGroup; defaultOpen: boolean; canEdit: boolean; busyId?: string; onStatus: (id: string, status: Status) => void }) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const { check, issues, amount } = group;
  const list = useProgressiveList(issues);
  const cols = columnsFor(group);
  return <article className="overflow-hidden rounded-xl border border-surface-border">
    <button type="button" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)} className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left hover:bg-surface-muted/50">
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SEVERITY_TONE[check.severity]}`}>{SEVERITY_LABEL[check.severity]}</span>
      <span className="min-w-0 flex-1 font-semibold text-gray-900">{check.title}</span>
      {amount != null && <span className="text-sm font-semibold text-gray-700 tabular-nums">{fmtBRL(amount)}</span>}
      <span className="rounded-full bg-surface-muted px-2.5 py-0.5 text-xs font-semibold text-gray-600 tabular-nums">{issues.length.toLocaleString("pt-BR")}</span>
      <IconChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && <>
      {(check.why || check.action) && <div className="grid gap-4 border-t border-surface-border bg-surface-muted/40 px-5 py-4 text-sm md:grid-cols-2">
        <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Por que importa</p><p className="mt-1 text-gray-700">{check.why}</p></div>
        <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">O que fazer</p><p className="mt-1 text-gray-700">{check.action}</p>{check.href && <Link to={check.href} className="mt-2 inline-block text-sm font-medium text-tm-700 underline">{check.hrefLabel}</Link>}</div>
      </div>}
      <div className="overflow-x-auto border-t border-surface-border">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500"><tr>{cols.map((c, k) => <th key={c.label} className={`p-3 ${k === 0 ? "pl-5" : ""} ${c.right ? "text-right" : ""}`}>{c.label}</th>)}{canEdit && <th className="p-3 pr-5 text-right">Tratar</th>}</tr></thead>
          <tbody className="divide-y divide-surface-border">
            {list.visible.map((i) => {
              return <tr key={i.id} className="align-top">
                {cols.map((c, k) => <td key={c.label} className={`p-3 ${k === 0 ? "pl-5" : ""} ${c.right ? "text-right" : ""}`}>{c.cell(i)}</td>)}
                {canEdit && <td className="whitespace-nowrap p-3 pr-5 text-right">
                  <button className="text-xs font-semibold text-tm-700 hover:underline disabled:opacity-50" disabled={busyId === i.id} onClick={() => onStatus(i.id, "resolved")}>Resolvido</button>
                  <span className="mx-2 text-gray-300">|</span>
                  <button className="text-xs font-semibold text-gray-500 hover:underline disabled:opacity-50" disabled={busyId === i.id} title="Não é erro: não volta nas próximas verificações" onClick={() => onStatus(i.id, "ignored")}>Ignorar</button>
                </td>}
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <div className="border-t border-surface-border"><LoadMoreBar list={list} label="registros" capped={false} /></div>
    </>}
  </article>;
}

function Treated({ issues, loading, canEdit, busyId, onReopen }: { issues?: DataQualityIssueDto[]; loading: boolean; canEdit: boolean; busyId?: string; onReopen: (id: string) => void }) {
  const rows = issues ?? [];
  const list = useProgressiveList(rows);
  if (loading) return <p role="status" className="text-sm text-gray-500">Carregando…</p>;
  if (!rows.length) return <p className="text-sm text-gray-500">Nenhum item marcado como resolvido ou ignorado.</p>;
  return <div className="overflow-hidden rounded-xl border border-surface-border">
    <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-left text-sm">
      <thead className="bg-surface-muted text-xs text-gray-500"><tr><th className="p-3 pl-5">Situação</th><th className="p-3">Verificação</th><th className="p-3">Detalhe</th>{canEdit && <th className="p-3 pr-5" />}</tr></thead>
      <tbody className="divide-y divide-surface-border">{list.visible.map((i) => <tr key={i.id} className="align-top">
        <td className="whitespace-nowrap p-3 pl-5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${i.status === "resolved" ? "bg-tm-50 text-tm-700" : "bg-gray-100 text-gray-600"}`}>{i.status === "resolved" ? "Resolvido" : "Ignorado"}</span></td>
        <td className="p-3 font-medium text-gray-800">{checkOf(i.issueType, i.severity).title}</td>
        <td className="p-3 text-gray-600">{i.description}</td>
        {canEdit && <td className="whitespace-nowrap p-3 pr-5 text-right"><button className="text-xs font-semibold text-tm-700 hover:underline disabled:opacity-50" disabled={busyId === i.id} onClick={() => onReopen(i.id)}>Reabrir</button></td>}
      </tr>)}</tbody>
    </table></div>
    <div className="border-t border-surface-border"><LoadMoreBar list={list} label="itens" capped={false} /></div>
  </div>;
}
